// src/components/ZakahCalculator.jsx
import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';
import Retirement401kCalculator from './401kCalculator'; // Import the 401kCalculator component
import { useNavigate } from 'react-router-dom';
import './ZakahCalculator.css';
import { motion } from 'framer-motion';

const ZakahCalculator = () => {
  const navigate = useNavigate(); // Initialize useNavigate hook
  const [goldPrice, setGoldPrice] = useState(null);
  const [silverPrice, setSilverPrice] = useState(null);
  const [date, setDate] = useState(null);
  const [formData, setFormData] = useState({
    currentaccount: '',
    savingsaccount: '',
    cash: '',
    gold: '',
    silver: '',
    debts: '',
    shares: '',
    '401k': '',
    ira: '',
    crypto: '',
    property: '',
    rental: '',
    businesscash: '',
    work: '',
    receivables: '',
    living: '',
    debt: '',
    personaldebt: '',
    mahr: '',
    bills: '',
    loans: '',
    longterm: '',
    credit: '',
    zakahpaid: '',
  });
  const [totalMetalsValue, setTotalMetalsValue] = useState(0);
  const [totalZakatableAssets, setTotalZakatableAssets] = useState(0);
  const [totalDebts, setTotalDebts] = useState(0);
  const [netzakatableassets, setNetZakatableAssets] = useState(0);
  const [zakah, setZakah] = useState(null);

  // Fetch gold and silver prices from Firestore
  useEffect(() => {
    const fetchPrices = async () => {
      try {
        console.log('Fetching metal prices...');
        const metalsCollection = collection(db, 'metals');
        const metalsSnapshot = await getDocs(metalsCollection);
        metalsSnapshot.forEach((doc) => {
          if (doc.id === 'gold') {
            console.log('Setting gold price:', doc.data().price);
            setGoldPrice(doc.data().price);
          } else if (doc.id === 'silver') {
            console.log('Setting silver price:', doc.data().price);
            setSilverPrice(doc.data().price);
          } else if (doc.id === 'date') {
            const timestamp = doc.data().time;
            const date = timestamp.toDate(); // Convert Firestore Timestamp to JavaScript Date
            console.log('Setting date:', date);
            setDate(date);
          }
        });
      } catch (error) {
        console.error('Error fetching metal prices or date:', error);
      }
    };

    fetchPrices();
  }, []);

  useEffect(() => {
    // Calculate the total zakatable assets whenever any input changes
    const newTotalZakatableAssets = calculateTotalZakatableAssets();
    const newTotalDebts = calculateTotalDebts();
    setTotalZakatableAssets(newTotalZakatableAssets);
    setTotalDebts(newTotalDebts);
    calculateNetZakatableAssets(newTotalZakatableAssets, newTotalDebts);
  }, [formData, totalMetalsValue]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    if (name === 'gold' || name === 'silver') {
      calculateTotalMetalsValue(name === 'gold' ? value : formData.gold, name === 'silver' ? value : formData.silver);
    }
  };

  const calculateTotalMetalsValue = (goldGrams, silverGrams) => {
    const goldValue = parseFloat(goldGrams || 0) * (goldPrice || 0) * 0.8;
    const silverValue = parseFloat(silverGrams || 0) * (silverPrice || 0);
    const totalValue = goldValue + silverValue;

    setTotalMetalsValue(totalValue.toFixed(2));
  };


  const calculateTotalZakatableAssets = () => {
    const {
      currentaccount,
      savingsaccount,
      cash,
      debts,
      shares,
      '401k': _401k,
      ira,
      crypto,
      property,
      rental,
      businesscash,
      work,
      receivables
    } = formData;

    const totalAssets =
      parseFloat(currentaccount || 0) +
      parseFloat(savingsaccount || 0) +
      parseFloat(cash || 0) +
      parseFloat(totalMetalsValue || 0) +
      parseFloat(debts || 0) +
      parseFloat(shares || 0) +
      parseFloat(_401k || 0) +
      parseFloat(ira || 0) +
      parseFloat(crypto || 0) +
      parseFloat(property || 0) +
      parseFloat(rental || 0) +
      parseFloat(businesscash || 0) +
      parseFloat(work || 0) +
      parseFloat(receivables || 0);

      return totalAssets;
  };

  const calculateTotalDebts = () => {
    const {
      living,
      debt,
      personaldebt,
      mahr,
      bills,
      loans,
      longterm,
      credit
    } = formData;

    const totalDebts =
      parseFloat(living || 0) +
      parseFloat(debt || 0) +
      parseFloat(personaldebt || 0) +
      parseFloat(mahr || 0) +
      parseFloat(bills || 0) +
      parseFloat(loans || 0) +
      parseFloat(longterm || 0) +
      parseFloat(credit || 0);

      return totalDebts;
  };

  const calculateNetZakatableAssets = (totalZakatableAssets, totalDebts) => {
    console.log(totalZakatableAssets, totalDebts);
    const netAssets = totalZakatableAssets - totalDebts;
    setNetZakatableAssets(netAssets.toFixed(3));
  };


  const calculateZakah = () => {
    const zakahAmount = parseFloat(netzakatableassets || 0) * 0.025;
    const zakahDue = zakahAmount - parseFloat(formData.zakahpaid || 0);
    
    // If the Zakah due is less than 0, set it to 0
    const finalZakahDue = zakahDue < 0 ? 0 : zakahDue;
  
    setZakah(finalZakahDue.toFixed(3));
  };
  

  const pageVariants = {
    initial: {
      opacity: 0,
      y: 20
    },
    animate: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    },
    exit: {
      opacity: 0,
      y: -20,
      transition: {
        duration: 0.4
      }
    }
  };

  const contentVariants = {
    initial: {
      opacity: 0,
      y: 20
    },
    animate: {
      opacity: 1,
      y: 0,
      transition: {
        delay: 0.2,
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  return (
    <motion.div
      initial="initial"
      animate="animate"
      exit="exit"
      variants={pageVariants}
      style={{ backgroundColor: '#0c1a2b', color: '#ffffff', minHeight: '300vh', paddingTop: '20px', paddingLeft: '20px' }}
    >
      <motion.button
        variants={contentVariants}
        onClick={() => navigate('/')}
        style={{
          margin: '10px',
          padding: '15px 25px',
          borderRadius: '10px',
          fontSize: '16px',
          fontWeight: 'bold',
          color: 'white',
          backgroundColor: '#333',
          border: 'none',
          cursor: 'pointer',
          transition: 'transform 0.3s ease, box-shadow 0.3s ease, background-color 0.3s ease'
        }}
        whileHover={{ 
          scale: 1.05,
          boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
          backgroundColor: '#f0e68c',
          color: '#0c1a2b'
        }}
        whileTap={{ 
          scale: 0.98,
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)'
        }}
      >
        Go Back to Main Page
      </motion.button>

      <motion.div
        variants={contentVariants}
      >
        <br />
        <br />
        <p>Disclaimer: Use a PC/Mac for best experience. None of the information from the calculator is being stored by the Tampa Zakah Fund for any purpose</p>
        <p>Use the 401k calculator if you wish to calculate the zakah on your 401k</p>
        <Retirement401kCalculator />

        <h2>Find out if you are eligible to pay zakah using the Zakah Calculator</h2>
        
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
        >
          <h2>Step 1. Zakatable Assets(What you have) </h2>
          <div>
            <h3>1. Cash </h3>
            <label>Current account: </label>
            <input type="number" name="currentaccount" value={formData.currentaccount} onChange={handleChange} className="input-field" />
          </div>
          <div>
           
            <label>Savings account: </label>
            <input type="number" name="savingsaccount" value={formData.savingsaccount} onChange={handleChange} className="input-field" />
          </div>
          <div>
           
            <label>Cash in Hand: </label>
            <input type="number" name="cash" value={formData.cash} onChange={handleChange} className="input-field"/>
          </div>

          {/* Gold and Silver Section */}
          <h3>2. Gold and Silver </h3>
          <div>
            <label>Gold (grams): </label>
            <input type="number" name="gold" value={formData.gold} onChange={handleChange} className="input-field"/>
          </div>
          <div>
            <label>Silver (grams): </label>
            <input type="number" name="silver" value={formData.silver} onChange={handleChange} className="input-field"/>
          </div>
          <div>
            <label>Total Value of Gold and Silver ($): </label>
            <input type="number" name="totalmetals" value={totalMetalsValue} readOnly className="input-field"/>
          </div>

          <div>
            <h3>3. Debts owed to you </h3>
            <label>Total amount of money lent that I am assured will be repaid </label>
            <input type="number" name="debts" value={formData.debts} onChange={handleChange} className="input-field"/>
          </div>
          <h3>4. Investments - Shares, Mutual Funds, 401k, IRA </h3>
          <div>
            <label>Short term shares for capital gain $ </label>
            <input type="number" name="shares" value={formData.shares} onChange={handleChange} className="input-field"/>
          </div>
          <div>
            <label>401k(use the calculator above and fill this out with the net withdrawal amount) $</label>
            <input type="number" name="401k" value={formData['401k']} onChange={handleChange} className="input-field"/>
          </div>
          <div>
            <label>Roth IRA </label>
            <input type="number" name="ira" value={formData.ira} onChange={handleChange} className="input-field"/>
          </div>
          <div>
            <label> Cryptocurrencies </label>
            <input type="number" name="crypto" value={formData.crypto} onChange={handleChange} className="input-field"/>
          </div>

          <h3>5. Property and Fixed Assets </h3>
          <div>
            <label>Property marked for Sale $</label>
            <input type="number" name="property" value={formData.property} onChange={handleChange} className="input-field"/>
          </div>
          <div>
            <label>Saved rental income $</label>
            <input type="number" name="rental" value={formData.rental} onChange={handleChange} className="input-field"/>
          </div>

          <h3>6. Business Assets</h3>
          <div>
            <label> Total Cash in my Business </label>
            <input type="number" name="businesscash" value={formData.businesscash} onChange={handleChange} className="input-field"/>
          </div>
          <div>
            <label> Value of Work in Progress </label>
            <input type="number" name="work" value={formData.work} onChange={handleChange} className="input-field"/>
          </div>
          <div>
            <label> Account receivables </label>
            <input type="number" name="receivables" value={formData.receivables} onChange={handleChange} className="input-field"/>
          </div>
          <div>
            <h3> Total Zakatable Assets $</h3>
            <input type="number" name="totalzakatableassets" value={totalZakatableAssets} readOnly className="input-field"/>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.6, duration: 0.6 }}
        >
          <h2>Step 2 - Deductible Expenses & debts (What you owe) </h2>
          <h3>7. Personal debts</h3>
          <div>
            <label> Overdue living expenses(1 month) </label>
            <input type="number" name="living" value={formData.living} onChange={handleChange} className="input-field"/>
          </div>
          <div>
            <label>Non-interest portion of debt(current month) </label>
            <input type="number" name="debt" value={formData.debt} onChange={handleChange} className="input-field"/>
          </div>
          <div>
            <label>Personal debts </label>
            <input type="number" name="personaldebt" value={formData.personaldebt} onChange={handleChange} className="input-field"/>
          </div>
          <div>
            <label>Outstanding dowry(Mahr) owed </label>
            <input type="number" name="mahr" value={formData.mahr} onChange={handleChange} className="input-field"/>
          </div>

          <h3>8. Business debts</h3>
          <div>
            <label> Overdue rent, bills, and salaries(current month) </label>
            <input type="number" name="bills" value={formData.bills} onChange={handleChange} className="input-field"/>
          </div>

          <div>
            <label>Outstanding short term loans(current month) </label>
            <input type="number" name="loans" value={formData.loans} onChange={handleChange} className="input-field"/>
          </div>
          <div>
            <label>Non interest portion of long term debt(current month) </label>
            <input type="number" name="longterm" value={formData.longterm} onChange={handleChange} className="input-field"/>
          </div>

          <div>
            <label>Value of goods purchased on credit </label>
            <input type="number" name="credit" value={formData.credit} onChange={handleChange} className="input-field"/>
          </div>
          <div>
            <h3> Total debts $</h3>
            <input type="number" name="totaldebts" value={totalDebts} readOnly className="input-field"/>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.8, duration: 0.6 }}
        >
          <h2>Step 3 - Zakah Calculation</h2>
          <div>
            <label>Nisab(Silver) </label>
            <input type="number" name="nisabsilver" value={silverPrice*595} readOnly className="input-field"/>
          </div>
          <div>
            <label>Nisab(Gold) </label>
            <input type="number" name="nisabgold" value={goldPrice*85} readOnly className="input-field"/>
          </div>

         
          <div>
            <h3>Net Zakatable Assets: </h3>
            <label> $</label>
            <input type="number" name="netzakatableassets" value={netzakatableassets} readOnly className="input-field"/>
          </div>
          <div> 
            <label> Zakat Already Paid </label>
            <input type="number" name="zakahpaid" value={formData.zakahpaid} onChange={handleChange} className="input-field"/>
          </div>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={calculateZakah}
            style={{
              margin: '10px',
              padding: '15px 25px',
              borderRadius: '10px',
              fontSize: '16px',
              fontWeight: 'bold',
              color: 'white',
              backgroundColor: '#333',
              border: 'none',
              cursor: 'pointer',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease, background-color 0.3s ease'
            }}
          >
            Calculate Zakah
          </motion.button>

          {zakah !== null && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h3>Total Zakah Due: ${zakah}</h3>
            </motion.div>
          )}
          <div style={{ color: '#FFD700', marginTop: '10px' }}>
            <p>Gold Price (per gram): ${goldPrice !== null ? goldPrice : 'Loading...'}</p>
            <p>Silver Price (per gram): ${silverPrice !== null ? silverPrice : 'Loading...'}</p>
            <p>Prices updated on: {date !== null ? date.toLocaleString() : 'Loading...'}</p> 
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1, duration: 0.6 }}
        >
          <div style={{ marginTop: '20px', padding: '10px', paddingBottom: '20px' }}>
            <h1>Donate to Tampa Zakah Fund the Zakah you just calculated or to Sadaqa funds</h1>

            <button 
              className="donate-button primary-button"
              onClick={() => {
                window.open('https://www.zeffy.com/en-US/donation-form/tampa-zakah-fundzakah-funds-only', '_blank');
              }}
            >
              Pay your Zakah
            </button>  

            <button 
              className="donate-button primary-button"
              onClick={() => window.open('https://www.zeffy.com/en-US/donation-form/tampa-zakah-fundsadaqa-funds-only', '_blank')}
            >
              Donate to Sadaqa Funds
            </button>
          </div>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export default ZakahCalculator;
